import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { TitleHolder } from "../../../common/sharedComponents/styles/card.style";
import { StepperContentHolder } from "../../vendorRegistration/vendorRegistration";
import {
  Button,
  Grid,
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  CustomTableContainer,
  CustomTableHead,
  TableHeaderFont,
} from "../../../styles/tableContent.style";
import moment from "moment";
import { getAdminRequestDetailsThunk, reviewVendorDocThunk } from "../redux/documentRequestsSlice";

const AdminRequestDetailView = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { requestDetails, loading } = useSelector((state) => state.documentRequests);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedMappingId, setSelectedMappingId] = useState(null);
  const [rejectionComments, setRejectionComments] = useState("");

  const fetchDetails = () => {
    dispatch(getAdminRequestDetailsThunk(id));
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line
  }, [id]);

  const handleApprove = (mappingId) => {
    if (window.confirm("Are you sure you want to approve this document?")) {
      dispatch(
        reviewVendorDocThunk({
          vendor_document_request_id: mappingId,
          status: "APPROVED",
        })
      ).then((res) => {
        if (res?.payload?.success) {
          fetchDetails();
        }
      });
    }
  };

  const handleOpenRejectDialog = (mappingId) => {
    setSelectedMappingId(mappingId);
    setRejectionComments("");
    setRejectDialogOpen(true);
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setSelectedMappingId(null);
  };

  const handleRejectSubmit = () => {
    if (!rejectionComments.trim()) {
      alert("Please enter a reason for rejection");
      return;
    }

    dispatch(
      reviewVendorDocThunk({
        vendor_document_request_id: selectedMappingId,
        status: "REJECTED",
        comments: rejectionComments,
      })
    ).then((res) => {
      if (res?.payload?.success) {
        handleCloseRejectDialog();
        fetchDetails();
      }
    });
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "APPROVED":
        return <Chip label="Approved" color="success" size="small" />;
      case "REJECTED":
        return <Chip label="Rejected" color="error" size="small" />;
      case "UPLOADED":
        return <Chip label="Pending Review" color="warning" size="small" />;
      default:
        return <Chip label="Pending Upload" color="default" size="small" />;
    }
  };

  if (loading && !requestDetails) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <CircularProgress />
      </div>
    );
  }

  if (!requestDetails) {
    return (
      <div style={{ padding: "2rem" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/app/document-requests")}>
          Back to list
        </Button>
        <p>Request not found.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ padding: "1rem 0" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/app/document-requests")}
          variant="text"
          color="primary"
        >
          Back to Requests
        </Button>
      </div>

      <Card style={{ padding: "1.5rem", marginBottom: "1.5rem" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <h2 style={{ margin: "0 0 0.5rem 0", fontWeight: 600 }}>{requestDetails.document_name}</h2>
            <p style={{ margin: "0 0 1rem 0", color: "#666" }}>
              {requestDetails.description || "No description provided."}
            </p>
          </Grid>
          <Grid item xs={12} md={4} style={{ display: "flex", flexDirection: "column", gap: "0.25rem", borderLeft: "1px solid #eee" }}>
            <div>
              <strong>Requested By: </strong>
              {requestDetails.Requester
                ? `${requestDetails.Requester.first_name} ${requestDetails.Requester.last_name}`
                : "-"}
            </div>
            <div>
              <strong>Due Date: </strong>
              {requestDetails.due_date ? moment(requestDetails.due_date).format("DD MMM YYYY") : "No Limit"}
            </div>
            <div>
              <strong>Mandatory: </strong>
              {requestDetails.is_required ? "Yes" : "No"}
            </div>
            <div>
              <strong>Request Date: </strong>
              {moment(requestDetails.createdAt).format("DD MMM YYYY")}
            </div>
          </Grid>
        </Grid>
      </Card>

      <TitleHolder style={{ paddingBottom: "0" }}>
        Target Vendors & Submission Details
      </TitleHolder>
      <br />

      <StepperContentHolder>
        <CustomTableContainer component={Card}>
          <Table aria-label="simple table">
            <CustomTableHead>
              <TableRow>
                <TableCell>
                  <TableHeaderFont>Sr. No.</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Vendor Name</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Vendor Code</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Email</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Status</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Uploaded File</TableHeaderFont>
                </TableCell>
                <TableCell>
                  <TableHeaderFont>Comments/Rejection Reason</TableHeaderFont>
                </TableCell>
                <TableCell align="center">
                  <TableHeaderFont>Actions</TableHeaderFont>
                </TableCell>
              </TableRow>
            </CustomTableHead>

            <TableBody>
              {requestDetails.VendorRequests && requestDetails.VendorRequests.length > 0 ? (
                requestDetails.VendorRequests.map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {index + 1}
                    </TableCell>
                    <TableCell style={{ fontWeight: 500 }}>
                      {item.Vendor ? item.Vendor.company_name : "-"}
                    </TableCell>
                    <TableCell>{item.Vendor ? item.Vendor.vendor_code : "-"}</TableCell>
                    <TableCell>{item.Vendor ? item.Vendor.company_email : "-"}</TableCell>
                    <TableCell>{getStatusChip(item.status)}</TableCell>
                    <TableCell>
                      {item.uploaded_file_url ? (
                        <a
                          href={item.uploaded_file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "underline" }}
                        >
                          {item.uploaded_file_name || "View Document"}
                        </a>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell style={{ color: "#666", fontSize: "0.85rem" }}>
                      {item.comments || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {item.status === "UPLOADED" ? (
                        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleApprove(item.id)}
                            style={{ textTransform: "capitalize" }}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => handleOpenRejectDialog(item.id)}
                            style={{ textTransform: "capitalize" }}
                          >
                            Reject
                          </Button>
                        </div>
                      ) : (
                        <span style={{ fontSize: "0.85rem", color: "#888" }}>
                          {item.status === "PENDING" ? "Awaiting Upload" : "Reviewed"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No vendors associated with this request.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CustomTableContainer>
      </StepperContentHolder>

      {/* Reject Comments Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Document Submission</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: "1rem" }}>
            Please provide a comment explaining why this document is being rejected. The vendor will see this feedback.
          </DialogContentText>
          <TextField
            autoFocus
            label="Rejection Comments"
            placeholder="e.g., File is blurry, Incorrect document format, Document expired..."
            type="text"
            fullWidth
            multiline
            rows={3}
            value={rejectionComments}
            onChange={(e) => setRejectionComments(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleRejectSubmit} color="error" variant="contained">
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminRequestDetailView;
